import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeftRight, Loader2, Plus, Trash2, Check, Package, Search, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RARITY_COLORS = {
  common: { bg: 'bg-gray-100', text: 'text-gray-600', badge: 'bg-gray-500' },
  uncommon: { bg: 'bg-green-100', text: 'text-green-600', badge: 'bg-green-500' },
  rare: { bg: 'bg-blue-100', text: 'text-blue-600', badge: 'bg-blue-500' },
  epic: { bg: 'bg-purple-100', text: 'text-purple-600', badge: 'bg-purple-500' },
  legendary: { bg: 'bg-amber-100', text: 'text-amber-600', badge: 'bg-amber-500' },
};

const TradingMarketplace = () => {
  const [listings, setListings] = useState([]);
  const [myCollectibles, setMyCollectibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showListForm, setShowListForm] = useState(false);
  const [selectedCollectible, setSelectedCollectible] = useState('');
  const [askingRarity, setAskingRarity] = useState('rare');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [acceptingId, setAcceptingId] = useState(null);
  const [offerCollectibleId, setOfferCollectibleId] = useState('');
  const [showOfferFor, setShowOfferFor] = useState(null);

  const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');

  // Small helper — returns a fresh headers object derived from the tracked
  // `token`. Kept as a function (not a component-scoped const) so callers
  // don't accidentally close over a stale-render's headers, and so that
  // `useCallback` deps below stay narrowed to `token` alone.
  const buildHeaders = () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });

  const loadListings = useCallback(() => {
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    const url = filter ? `${API_URL}/api/engagement/marketplace?rarity=${filter}` : `${API_URL}/api/engagement/marketplace`;
    fetch(url, { headers }).then(r => r.json())
      .then(data => setListings(data.listings || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [filter, token]);

  const loadMyCollectibles = useCallback(() => {
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    fetch(`${API_URL}/api/collectibles/my`, { headers })
      .then(r => r.json())
      .then(data => setMyCollectibles((data.collectibles || []).filter(c => c.is_tradeable)))
      .catch(() => {});
  }, [token]);

  useEffect(() => { loadListings(); loadMyCollectibles(); }, [loadListings, loadMyCollectibles]);

  const handleList = async () => {
    if (!selectedCollectible) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/engagement/marketplace/list`, {
        method: 'POST', headers: buildHeaders(),
        body: JSON.stringify({ collectible_id: selectedCollectible, asking_rarity: askingRarity, description }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      setMessage({ type: 'success', text: 'Listed! Your collectible is now on the marketplace.' });
      setShowListForm(false);
      setSelectedCollectible(''); setDescription('');
      loadListings();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setSubmitting(false);
  };

  const handleAccept = async (listingId) => {
    if (!offerCollectibleId) return;
    setAcceptingId(listingId);
    try {
      const res = await fetch(`${API_URL}/api/engagement/marketplace/${listingId}/accept`, {
        method: 'POST', headers: buildHeaders(),
        body: JSON.stringify({ offer_collectible_id: offerCollectibleId }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Trade failed');
      setMessage({ type: 'success', text: 'Trade completed! Check your Vault for the new collectible.' });
      setShowOfferFor(null); setOfferCollectibleId('');
      loadListings(); loadMyCollectibles();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setAcceptingId(null);
  };

  const handleCancel = async (listingId) => {
    try {
      await fetch(`${API_URL}/api/engagement/marketplace/${listingId}`, { method: 'DELETE', headers: buildHeaders() });
      setMessage({ type: 'success', text: 'Listing cancelled.' });
      loadListings();
    } catch { setMessage({ type: 'error', text: 'Could not cancel listing' }); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="trading-marketplace">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ArrowLeftRight size={18} className="text-purple-500" />
            <h3 className="font-bold text-gray-900">Trading Marketplace</h3>
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{listings.length} active</span>
          </div>
          <button
            onClick={() => setShowListForm(!showListForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition-colors"
            data-testid="list-for-trade-btn"
          >
            <Plus size={14} /> List for Trade
          </button>
        </div>

        {/* Rarity filter */}
        <div className="flex gap-1.5">
          {['', 'uncommon', 'rare', 'epic', 'legendary'].map(r => (
            <button key={r} onClick={() => setFilter(r)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all ${filter === r ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              data-testid={`filter-${r || 'all'}`}
            >
              {r || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div className={`mx-5 mt-3 p-3 rounded-lg flex items-center gap-2 text-sm ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {message.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto text-gray-400 hover:text-gray-600">&times;</button>
        </div>
      )}

      {/* List Form */}
      {showListForm && (
        <div className="mx-5 mt-3 p-4 bg-purple-50 border border-purple-200 rounded-xl" data-testid="list-form">
          <h4 className="font-semibold text-gray-900 mb-3">List a Collectible</h4>
          {myCollectibles.length === 0 ? (
            <p className="text-sm text-gray-500">No tradeable collectibles in your vault. Earn more to start trading!</p>
          ) : (
            <div className="space-y-3">
              <select
                value={selectedCollectible}
                onChange={(e) => setSelectedCollectible(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                data-testid="select-collectible"
              >
                <option value="">Choose a collectible...</option>
                {myCollectibles.map(c => (
                  <option key={c.collectible_id} value={c.collectible_id}>
                    {c.title} ({(c.rarity || 'common').toUpperCase()})
                  </option>
                ))}
              </select>
              <div className="flex gap-2">
                <select
                  value={askingRarity}
                  onChange={(e) => setAskingRarity(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="asking-rarity"
                >
                  <option value="common">Want: Common+</option>
                  <option value="uncommon">Want: Uncommon+</option>
                  <option value="rare">Want: Rare+</option>
                  <option value="epic">Want: Epic+</option>
                  <option value="legendary">Want: Legendary</option>
                </select>
                <input
                  type="text" value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description (optional)"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="listing-description"
                />
              </div>
              <button
                onClick={handleList} disabled={!selectedCollectible || submitting}
                className="w-full py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-list-btn"
              >
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Listing...</> : <><Package size={14} /> List for Trade</>}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Listings */}
      {loading ? (
        <div className="py-12 text-center"><Loader2 size={24} className="animate-spin text-gray-300 mx-auto" /></div>
      ) : listings.length === 0 ? (
        <div className="py-12 text-center">
          <Package size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-sm text-gray-400">No active listings yet.</p>
          <p className="text-xs text-gray-300 mt-1">Be the first — list a collectible above!</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {listings.map(l => {
            const r = RARITY_COLORS[l.collectible_rarity] || RARITY_COLORS.common;
            const isShowingOffer = showOfferFor === l.listing_id;
            return (
              <div key={l.listing_id} className="px-5 py-3 hover:bg-gray-50/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-18 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                    {l.image_url ? <img src={l.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-14 h-18 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{l.collectible_title || 'Collectible'}</p>
                    <p className="text-xs text-gray-400">by {l.seller_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${r.bg} ${r.text}`}>
                        {(l.collectible_rarity || 'common').toUpperCase()}
                      </span>
                      <span className="text-[10px] text-gray-400">wants</span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${(RARITY_COLORS[l.asking_rarity] || RARITY_COLORS.common).bg} ${(RARITY_COLORS[l.asking_rarity] || RARITY_COLORS.common).text}`}>
                        {(l.asking_rarity || 'any').toUpperCase()}+
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => { setShowOfferFor(isShowingOffer ? null : l.listing_id); setOfferCollectibleId(''); }}
                      className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                      data-testid={`make-offer-${l.listing_id}`}
                    >
                      Make Offer
                    </button>
                  </div>
                </div>
                {/* Offer drawer */}
                {isShowingOffer && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid={`offer-drawer-${l.listing_id}`}>
                    <p className="text-xs text-gray-600 mb-2">Select your collectible to offer in exchange:</p>
                    <div className="flex gap-2">
                      <select
                        value={offerCollectibleId}
                        onChange={(e) => setOfferCollectibleId(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      >
                        <option value="">Choose your collectible...</option>
                        {myCollectibles.map(c => (
                          <option key={c.collectible_id} value={c.collectible_id}>
                            {c.title} ({(c.rarity || 'common').toUpperCase()})
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleAccept(l.listing_id)}
                        disabled={!offerCollectibleId || acceptingId === l.listing_id}
                        className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                        data-testid={`confirm-trade-${l.listing_id}`}
                      >
                        {acceptingId === l.listing_id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                        Trade
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* How Trading Works */}
      <div className="px-5 py-4 border-t border-gray-100 bg-gray-50">
        <p className="text-[11px] text-gray-400 text-center">
          <strong>How it works:</strong> List a collectible → Set your asking rarity → Someone offers a trade → Collectibles swap instantly. No fees, no hassle.
        </p>
      </div>
    </div>
  );
};

export default TradingMarketplace;
